import type { Coordinate2D, Coordinate3D, PixelArrayPoint } from '../types';

/**
 * Utility class for pixel array operations and coordinate transformations
 */
export class PixelArrayData {
  private pixelArray: PixelArrayPoint[];

  constructor(pixelArray: PixelArrayPoint[]) {
    this.pixelArray = pixelArray;
  }

  /**
   * Calculate 2D distance between two points
   */
  private distance(a: Coordinate2D, b: Coordinate2D): number {
    return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
  }

  /**
   * Calculate 3D distance between two points
   */
  distance3D(a: Coordinate3D, b: Coordinate3D): number {
    return Math.sqrt(
      Math.pow(a.x - b.x, 2) + 
      Math.pow(a.y - b.y, 2) + 
      Math.pow(a.z - b.z, 2)
    );
  }

  /**
   * Find the closest pixel and world coordinates to a given pixel point
   */
  findClosestCoord(pixelPoint: Coordinate2D): [Coordinate2D, Coordinate3D] {
    if (this.pixelArray.length === 0) {
      throw new Error("PixelArray is empty");
    }

    const closestPoint = this.pixelArray.reduce((closest, current) => 
      this.distance(current.pixelCoordinates, pixelPoint) < 
      this.distance(closest.pixelCoordinates, pixelPoint)
        ? current
        : closest
    );

    return [closestPoint.pixelCoordinates, closestPoint.worldCoordinates];
  }

  /**
   * Get the nearest image point (pixel coordinates) for a given world coordinate
   */
  getNearestImagePointByWorldCoord(worldCoord: Coordinate3D): Coordinate2D {
    if (this.pixelArray.length === 0) {
      throw new Error("PixelArray is empty");
    }

    const nearestPixel = this.pixelArray.reduce((nearest, current) => 
      this.distance3D(current.worldCoordinates, worldCoord) < 
      this.distance3D(nearest.worldCoordinates, worldCoord)
        ? current
        : nearest
    );

    return nearestPixel.pixelCoordinates;
  }
}